import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Users, Bot } from 'lucide-react';
import type { BrandingConfig } from '../../../helpers/useBranding';

interface PartnerLandingPageProps {
  branding: BrandingConfig;
}

export default function PartnerLandingPage({ branding }: PartnerLandingPageProps) {
  useEffect(() => {
    document.title = `${branding.brandName} | Powered by Tasking.tech`;
  }, [branding.brandName]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--background, #0a0a0b)', color: 'var(--foreground, #f8fafc)' }}>
      {/* Nav */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img
            src={branding.logoUrl}
            alt={branding.brandName}
            style={{ height: 36, borderRadius: 6 }}
          />
          <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>{branding.brandName}</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Link to="/login" style={{
            padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600,
            color: 'var(--foreground, #f8fafc)', border: '1px solid rgba(255,255,255,0.12)',
            textDecoration: 'none', transition: 'all 0.2s',
          }}>
            Sign In
          </Link>
          <Link to="/register" style={{
            padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600,
            color: '#fff', background: branding.primaryColor || '#f97316',
            textDecoration: 'none', transition: 'all 0.2s',
          }}>
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center', padding: '4rem 2rem',
        maxWidth: 800, margin: '0 auto', gap: '1.5rem',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.35rem 1rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
          background: 'rgba(249,115,22,0.1)', color: '#f97316',
          border: '1px solid rgba(249,115,22,0.2)',
        }}>
          <Bot size={14} /> Powered by Tasking.tech
        </div>

        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, lineHeight: 1.1,
          margin: 0, letterSpacing: '-0.02em',
        }}>
          AI-Powered Productivity
          <br />
          <span style={{ color: branding.primaryColor || '#f97316' }}>by {branding.brandName}</span>
        </h1>

        <p style={{
          fontSize: '1.125rem', color: 'var(--muted-foreground, #94a3b8)',
          maxWidth: 560, lineHeight: 1.7, margin: 0,
        }}>
          Deploy autonomous AI agents that plan, execute, and deliver work — with human oversight and enterprise-grade security built in.
        </p>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/register" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 2rem', borderRadius: 10, fontSize: '1rem', fontWeight: 600,
            color: '#fff', background: `linear-gradient(135deg, ${branding.primaryColor || '#f97316'}, ${branding.primaryColor || '#ea580c'})`,
            textDecoration: 'none', boxShadow: '0 4px 14px rgba(249,115,22,0.3)',
          }}>
            Start Free <ArrowRight size={18} />
          </Link>
          <Link to="/login" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 2rem', borderRadius: 10, fontSize: '1rem', fontWeight: 600,
            color: 'var(--foreground, #f8fafc)', border: '1px solid rgba(255,255,255,0.12)',
            textDecoration: 'none', background: 'rgba(255,255,255,0.04)',
          }}>
            Sign In
          </Link>
        </div>
      </section>

      {/* Features */}
      <section style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem', padding: '3rem 2rem', maxWidth: 1000, margin: '0 auto', width: '100%',
      }}>
        {[
          { icon: <Zap size={22} />, title: 'Autonomous Agents', desc: 'AI agents that plan, build, test, and ship autonomously with human approvals.' },
          { icon: <Users size={22} />, title: 'Team Collaboration', desc: 'Real-time collaboration with AI participants, personas, and multi-agent orchestration.' },
          { icon: <Shield size={22} />, title: 'Enterprise Security', desc: 'Full audit trails, governance controls, and data protection built in from day one.' },
        ].map((f, i) => (
          <div key={i} style={{
            padding: '1.5rem', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{ color: branding.primaryColor || '#f97316', marginBottom: '0.75rem' }}>{f.icon}</div>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.5rem' }}>{f.title}</h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--muted-foreground, #94a3b8)', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
          </div>
        ))}
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2rem', textAlign: 'center', fontSize: '0.75rem',
        color: 'var(--muted-foreground, #64748b)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}>
        <p style={{ margin: 0 }}>
          {branding.brandName} — Powered by{' '}
          <a href="https://tasking.tech" style={{ color: '#f97316', textDecoration: 'none' }}>Tasking.tech</a>
        </p>
      </footer>
    </div>
  );
}
